import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Download, CheckCircle, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConference } from '@/context/ConferenceContext';
import { requestBrochureDownload, getErrorMessage } from '@/services/api';

const brochureSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Invalid email address'),
  organization: z.string().min(2, 'Organization is required'),
  country: z.string().min(2, 'Country is required'),
});

const BrochureDownload = () => {
  const { conference, shortName, importantDetails, loading: confLoading, siteConfig } = useConference();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(brochureSchema),
  });

  const onSubmit = async (data) => {
    try {
      const brochureData = {
        ...data,
        conferenceId: shortName,
      };
      
      await requestBrochureDownload(brochureData);
      setIsSubmitted(true);
      toast.success('Brochure request submitted successfully!');
    } catch (error) {
      console.error('Brochure request error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  if (confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Brochure Request Submitted - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center py-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex w-20 h-20 rounded-full bg-green-100 items-center justify-center mb-6">
              <CheckCircle className="text-green-500" size={48} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Request Submitted!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for your interest in {siteConfig?.fullName || importantDetails?.ConferenceTitle || 'our conference'}.
            </p>
            <div className="bg-white p-6 rounded-2xl shadow-lg text-left space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">📧 What's Next?</h3>
              <p className="text-gray-700">
                • You will receive the conference brochure via email within 24 hours
              </p>
              <p className="text-gray-700">
                • Check your spam/junk folder if you don't see it in your inbox
              </p>
              <p className="text-gray-700">
                • For urgent requests, please contact us at {siteConfig?.contact?.email || 'info@conference.com'}
              </p>
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              className="mt-8 bg-sky-500 hover:bg-sky-600"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Download Brochure - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Download the conference brochure" />
      </Helmet>

      <div className="bg-gradient-to-br from-sky-50 to-white min-h-screen py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex w-16 h-16 bg-sky-100 rounded-2xl items-center justify-center mb-4">
              <FileText className="text-sky-500" size={32} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Download Conference Brochure</h1>
            <p className="text-gray-600">
              Get detailed information about {siteConfig?.fullName || importantDetails?.ConferenceTitle || 'our conference'} including schedule, speakers, and venue details.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="Enter your full name"
                  className="mt-1"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="your.email@example.com"
                  className="mt-1"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="organization">Organization *</Label>
                <Input
                  id="organization"
                  {...register('organization')}
                  placeholder="University/Company"
                  className="mt-1"
                />
                {errors.organization && (
                  <p className="text-red-500 text-sm mt-1">{errors.organization.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  {...register('country')}
                  placeholder="United States"
                  className="mt-1"
                />
                {errors.country && (
                  <p className="text-red-500 text-sm mt-1">{errors.country.message}</p>
                )}
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 hover:bg-sky-600 py-6 text-lg font-semibold"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Download className="mr-2" size={20} />
                      Request Brochure
                    </>
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-sky-50 rounded-xl">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> The brochure will be sent to your email address. 
                By submitting this form, you agree to receive conference-related communications.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BrochureDownload;
